import { useQuery } from '@tanstack/react-query';
import {
  getSubscription,
  getUsage,
  getInvoices,
  type Subscription,
  type UsageData,
  type Invoice,
} from '../api/client';
import { CreditCard, ArrowUpRight, XCircle, Receipt } from 'lucide-react';
import clsx from 'clsx';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function usageColor(percent: number): string {
  if (percent >= 80) return 'bg-red-500';
  if (percent >= 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

function usageTextColor(percent: number): string {
  if (percent >= 80) return 'text-red-600';
  if (percent >= 60) return 'text-yellow-600';
  return 'text-green-600';
}

function statusBadge(status: Invoice['status']) {
  const map: Record<Invoice['status'], string> = {
    paid: 'bg-green-100 text-green-700',
    draft: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-700',
    refunded: 'bg-blue-100 text-blue-700',
    void: 'bg-gray-100 text-gray-500',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
}

function subscriptionStatusBadge(status: Subscription['status']) {
  const map: Record<Subscription['status'], string> = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-yellow-100 text-yellow-700',
    canceled: 'bg-red-100 text-red-700',
    paused: 'bg-gray-100 text-gray-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
}

export default function BillingPage() {
  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscription,
  });

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['usage'],
    queryFn: getUsage,
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: getInvoices,
  });

  const isLoading = subLoading || usageLoading || invoicesLoading;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const planName = subscription?.plan?.name || 'Free';
  const priceCents =
    subscription?.billingCycle === 'annual'
      ? subscription?.plan?.annualPriceCents
      : subscription?.plan?.monthlyPriceCents;
  const billingCycleLabel = subscription?.billingCycle === 'annual' ? '/yr' : '/mo';
  const percentUsed = usage?.percentUsed ?? 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Billing</h1>

      {/* Top row: Plan + Usage */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Current Plan */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-brand-600" />
            <h3 className="text-sm font-semibold text-gray-900">Current Plan</h3>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{planName}</span>
            {subscription && (
              <span
                className={clsx(
                  'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                  subscriptionStatusBadge(subscription.status),
                )}
              >
                {subscription.status}
              </span>
            )}
          </div>

          {priceCents != null && (
            <p className="mt-1 text-sm text-gray-500">
              {formatPrice(priceCents)}
              {billingCycleLabel}
            </p>
          )}

          {subscription?.currentPeriodEnd && (
            <p className="mt-1 text-xs text-gray-400">
              Next billing date: {formatDate(subscription.currentPeriodEnd)}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button className="btn-primary flex items-center gap-1 text-sm">
              <ArrowUpRight className="h-4 w-4" />
              Change Plan
            </button>
            <button className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50">
              Update Payment Method
            </button>
            <button className="flex items-center gap-1 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
              <XCircle className="h-4 w-4" />
              Cancel Subscription
            </button>
          </div>
        </div>

        {/* Usage Meter */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand-600" />
            <h3 className="text-sm font-semibold text-gray-900">Monthly Usage</h3>
          </div>

          <div className="mb-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">
              {(usage?.sessionCount ?? 0).toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">
              of {(usage?.sessionLimit ?? 0).toLocaleString()} sessions
            </span>
          </div>

          <div className="mb-3 h-4 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={clsx('h-full rounded-full transition-all', usageColor(percentUsed))}
              style={{ width: `${Math.min(percentUsed, 100)}%` }}
            />
          </div>

          <p className={clsx('text-sm font-medium', usageTextColor(percentUsed))}>
            {percentUsed.toFixed(1)}% used this billing period
          </p>

          {usage?.periodStart && usage?.periodEnd && (
            <p className="mt-1 text-xs text-gray-400">
              {formatDate(usage.periodStart)} — {formatDate(usage.periodEnd)}
            </p>
          )}

          {usage?.limitReached && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              Session limit reached. New sessions are not being recorded. Upgrade your plan to continue tracking.
            </div>
          )}
        </div>
      </div>

      {/* Invoice History */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Invoice History</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="table-header">Date</th>
                <th className="table-header">Period</th>
                <th className="table-header text-right">Amount</th>
                <th className="table-header text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="table-cell font-medium">{formatDate(invoice.createdAt)}</td>
                  <td className="table-cell text-gray-500">
                    {invoice.periodStart && invoice.periodEnd
                      ? `${formatDate(invoice.periodStart)} — ${formatDate(invoice.periodEnd)}`
                      : '—'}
                  </td>
                  <td className="table-cell text-right font-medium">
                    {formatPrice(invoice.amountCents)}
                  </td>
                  <td className="table-cell text-right">
                    <span
                      className={clsx(
                        'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                        statusBadge(invoice.status),
                      )}
                    >
                      {invoice.status}
                    </span>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    No invoices yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
